import { useCallback, useEffect, useState } from 'react'
import api from '../../../helpers/apiRequester'

export interface PayableClaimsResponse {
    introducer: string
    totalClaims: number
    sixYearClaims: number
    fifteenYearClaims: number
    fundableClients: number
    calculatedAt: string
}

interface UsePayableClaimsResult {
    data: PayableClaimsResponse | null
    loading: boolean
    error: string | null
    reload: () => void
}

// Shared fetch for the `/introducer-payable-claims` endpoint used by the
// admin-only "Payable Claims Progress" section. Mirrors `useFundableClients`:
// `enabled` lets callers skip the (heavy) request when the section isn't shown
// (e.g. for non-Sentinel users), and `cohort` scopes the count to a PCP
// Referral campaign window when supplied.
export function usePayableClaims(introducer?: string, enabled = true, cohort?: string): UsePayableClaimsResult {
    const shouldFetch = enabled && !!introducer
    const [loading, setLoading] = useState<boolean>(shouldFetch)
    const [error, setError] = useState<string | null>(null)
    const [data, setData] = useState<PayableClaimsResponse | null>(null)

    const load = useCallback(async () => {
        if (!enabled || !introducer) return
        setLoading(true)
        setError(null)
        try {
            const res = await api({
                url: '/introducer-payable-claims',
                data: cohort ? { introducer, cohort } : { introducer },
            })
            if (res.data?.success && res.data?.data) {
                setData(res.data.data as PayableClaimsResponse)
            } else {
                setError(res.data?.message || 'Failed to load payable claims')
            }
        } catch (e: any) {
            setError(e?.response?.data?.message || e?.message || 'Failed to load payable claims')
        } finally {
            setLoading(false)
        }
    }, [introducer, enabled, cohort])

    useEffect(() => {
        if (shouldFetch) load()
    }, [load, shouldFetch])

    return { data, loading, error, reload: load }
}
